import React from 'react';

interface ModificationItem {
  id?: number;
  targetFile: string;
  targetFeatureId: string;
  fieldPath: string;
  oldValue: any;
  newValue: any;
  operationType: 'create' | 'update' | 'delete';
  geometry?: any;
}

interface ProposalPreviewProps {
  modificationItems: ModificationItem[];
  onItemClick?: (item: ModificationItem) => void;
  onItemDelete?: (itemId: number) => void;
  readonly?: boolean;
}

const ProposalPreview: React.FC<ProposalPreviewProps> = ({
  modificationItems,
  onItemClick,
  onItemDelete,
  readonly = false
}) => {
  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOperationText = (operation: string) => {
    switch (operation) {
      case 'create':
        return '新增';
      case 'update':
        return '修改';
      case 'delete':
        return '刪除';
      default:
        return '未知';
    }
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    
    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-50 p-2 rounded max-w-xs overflow-hidden">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span className="break-words">{String(value)}</span>;
  };

  const getFieldDisplayName = (fieldPath: string) => {
    const parts = fieldPath.split('.');
    return parts.slice(1).join('.'); // 移除 'properties' 前綴
  };

  if (modificationItems.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>尚無修改項目</p>
        <p className="text-sm mt-1">在地圖上選取特徵並編輯屬性來新增修改項目</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-medium text-gray-900">修改項目預覽</h3>
        <p className="text-sm text-gray-600">總計 {modificationItems.length} 項修改</p>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-3">
          {modificationItems.map((item, index) => (
            <div
              key={item.id || index}
              onClick={() => onItemClick?.(item)}
              className={`border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors ${
                onItemClick ? 'cursor-pointer' : ''
              }`}
            >
              {/* 操作類型和檔案資訊 */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getOperationColor(item.operationType)}`}>
                    {getOperationText(item.operationType)}
                  </span>
                  <span className="text-sm text-gray-600">{item.targetFile}</span>
                </div>
                {!readonly && onItemDelete && item.id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemDelete(item.id!);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm p-1"
                    title="刪除修改項目"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* 特徵和欄位資訊 */}
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-gray-500">特徵 ID:</span>
                  <span className="ml-1 font-mono text-xs">{item.targetFeatureId}</span>
                </div>
                <div>
                  <span className="text-gray-500">欄位:</span>
                  <span className="ml-1 font-medium">{getFieldDisplayName(item.fieldPath)}</span>
                </div>
              </div>

              {/* 值變更資訊 */}
              <div className="mt-3 space-y-2">
                {item.operationType === 'update' && (
                  <>
                    <div>
                      <span className="text-xs text-gray-500 block">原始值:</span>
                      <div className="mt-1">{formatValue(item.oldValue)}</div>
                    </div>
                    <div className="border-l-2 border-blue-300 pl-2">
                      <span className="text-xs text-blue-600 block">新值:</span>
                      <div className="mt-1">{formatValue(item.newValue)}</div>
                    </div>
                  </>
                )}
                
                {item.operationType === 'create' && (
                  <div className="border-l-2 border-green-300 pl-2">
                    <span className="text-xs text-green-600 block">新增值:</span>
                    <div className="mt-1">{formatValue(item.newValue)}</div>
                  </div>
                )}
                
                {item.operationType === 'delete' && (
                  <div className="border-l-2 border-red-300 pl-2">
                    <span className="text-xs text-red-600 block">刪除值:</span>
                    <div className="mt-1">{formatValue(item.oldValue)}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 統計資訊 */}
      <div className="border-t bg-gray-50 p-3">
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div>
            <div className="font-medium text-green-600">
              {modificationItems.filter(item => item.operationType === 'create').length}
            </div>
            <div className="text-gray-600">新增</div>
          </div>
          <div>
            <div className="font-medium text-blue-600">
              {modificationItems.filter(item => item.operationType === 'update').length}
            </div>
            <div className="text-gray-600">修改</div>
          </div>
          <div>
            <div className="font-medium text-red-600">
              {modificationItems.filter(item => item.operationType === 'delete').length}
            </div>
            <div className="text-gray-600">刪除</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalPreview;